import React, { useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { getAuthHeaders } from '../../../lib/api';
// import { supabase } from '../../../lib/supabase'; // only used by hidden Google Review warning
import { parseImportFile, summarizeColumnValues, OVERSIZE_ROW_WARNING } from '../../../utils/import-file-parser';
import { scoreImportRows } from '../../../utils/past-client-scorer';
import { importPastClients } from '../../../utils/import-past-clients';

const SYSTEM_FIELDS = [
  { value: '', label: 'Skip this column' },
  { value: 'name', label: 'Full Name (Required)' },
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'email', label: 'Email Address' },
  { value: 'phone', label: 'Phone Number' },
  { value: 'location', label: 'Address' },
  { value: 'address', label: 'Street Address' },
  { value: 'city', label: 'City' },
  { value: 'state', label: 'State' },
  { value: 'zipCode', label: 'Zip Code' },
  { value: 'lifecycleStage', label: 'Lifecycle Stage / Status' },
  { value: 'engagementLevel', label: 'Engagement Level' },
  { value: 'tags', label: 'Tags' },
  { value: 'notes', label: 'Notes' },
  { value: 'birthday', label: 'Birthday' },
  { value: 'homeClosingDate', label: 'Home Closing Date' },
];

const CLASSIFY_BATCH = 40;

const hasPhone = (c) => Boolean(String(c?.phone ?? '').trim());

const FindPastClientsWizard = ({ isOpen, onClose, onComplete, onSwitchClassic }) => {
  const [step, setStep] = useState(1); // 1 upload, 2 mapping, 3 analyzing, 4 review
  const [file, setFile] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [rows, setRows] = useState([]);
  const [oversizeWarning, setOversizeWarning] = useState(false);
  const [mappings, setMappings] = useState({});
  const [pastClientStageValues, setPastClientStageValues] = useState([]);
  const [mappingNotes, setMappingNotes] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [parseError, setParseError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [isBusy, setIsBusy] = useState(false);
  const [filterGroup, setFilterGroup] = useState('likely');
  const [onlyMissingPhone, setOnlyMissingPhone] = useState(false);
  // Google Review / AUTO Review Ask warning — temporarily hidden
  // const [autoReviewAskOn, setAutoReviewAskOn] = useState(false);
  // const [dismissReviewAskWarning, setDismissReviewAskWarning] = useState(false);

  // useEffect(() => {
  //   if (!isOpen) return;
  //   let cancelled = false;
  //   (async () => {
  //     try {
  //       const { data: { user } } = await supabase.auth.getUser();
  //       if (!user || cancelled) return;
  //       const { data: profile } = await supabase
  //         .from('user_profiles')
  //         .select('google_review_auto_on_new_contact')
  //         .eq('id', user.id)
  //         .single();
  //       if (!cancelled) {
  //         setAutoReviewAskOn(Boolean(profile?.google_review_auto_on_new_contact));
  //       }
  //     } catch {
  //       // Non-blocking — warning is optional
  //     }
  //   })();
  //   return () => { cancelled = true; };
  // }, [isOpen]);

  const reset = () => {
    setStep(1);
    setFile(null);
    setHeaders([]);
    setRows([]);
    setOversizeWarning(false);
    setMappings({});
    setPastClientStageValues([]);
    setMappingNotes('');
    setCandidates([]);
    setParseError(null);
    setStatusMessage('');
    setProgress({ done: 0, total: 0 });
    setIsBusy(false);
    setFilterGroup('likely');
    setOnlyMissingPhone(false);
    // setDismissReviewAskWarning(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileUpload = async (e) => {
    const selected = e?.target?.files?.[0];
    setParseError(null);
    if (!selected) return;

    setIsBusy(true);
    setStatusMessage('Parsing file…');
    try {
      const parsed = await parseImportFile(selected);
      setFile(selected);
      setHeaders(parsed.headers);
      setRows(parsed.rows);
      setOversizeWarning(parsed.oversizeWarning);
      setMappings({});
    } catch (err) {
      setParseError(err.message || 'Failed to parse file');
      setFile(null);
      setHeaders([]);
      setRows([]);
    } finally {
      setIsBusy(false);
      setStatusMessage('');
    }
  };

  const inferMapping = async () => {
    setIsBusy(true);
    setStatusMessage('AI is detecting columns…');
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch('/api/clients/infer-mapping', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          headers,
          sampleRows: rows.slice(0, 20),
          columnValues: summarizeColumnValues(headers, rows),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || data.message || 'Mapping inference failed');
      }
      setMappings(data.mappings || {});
      setPastClientStageValues(data.pastClientStageValues || []);
      setMappingNotes(data.notes || '');
      setStep(2);
    } catch (err) {
      console.error(err);
      setParseError(err.message || 'Could not infer column mapping');
    } finally {
      setIsBusy(false);
      setStatusMessage('');
    }
  };

  const hasNameMapping = useMemo(() => {
    const values = Object.values(mappings || {});
    return values.includes('name') || (values.includes('firstName') && values.includes('lastName'));
  }, [mappings]);

  const runClassification = async () => {
    if (!hasNameMapping) {
      alert('Please map a name field (Full Name, or First + Last Name) before continuing.');
      return;
    }

    setStep(3);
    setIsBusy(true);
    setStatusMessage('Scoring contacts…');

    try {
      let scored = scoreImportRows(headers, rows, mappings, { pastClientStageValues });
      const ambiguous = scored.filter((c) => c.needsLlm);

      if (ambiguous.length > 0) {
        setProgress({ done: 0, total: ambiguous.length });
        setStatusMessage(`AI is reviewing ${ambiguous.length} uncertain contact${ambiguous.length === 1 ? '' : 's'}…`);

        const authHeaders = await getAuthHeaders();
        const updates = new Map();

        for (let i = 0; i < ambiguous.length; i += CLASSIFY_BATCH) {
          const batch = ambiguous.slice(i, i + CLASSIFY_BATCH).map((c) => ({
            rowIndex: c.rowIndex,
            name: c.name,
            email: c.email,
            phone: c.phone,
            stage: c.stage,
            tags: c.tags,
            notes: c.notes,
            homeClosingDate: c.homeClosingDate,
            location: c.location,
          }));

          const res = await fetch('/api/clients/classify-import', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders },
            body: JSON.stringify({ rows: batch }),
          });
          const data = await res.json();
          if (res.ok && Array.isArray(data.results)) {
            for (const r of data.results) {
              updates.set(r.rowIndex, r);
            }
          }
          setProgress({ done: Math.min(i + CLASSIFY_BATCH, ambiguous.length), total: ambiguous.length });
        }

        scored = scored.map((c) => {
          if (!updates.has(c.rowIndex)) return c;
          const r = updates.get(c.rowIndex);
          return {
            ...c,
            confidence: r.isPastClient
              ? (r.confidence === 'low' ? 'medium' : r.confidence)
              : 'low',
            reason: r.reason,
            needsLlm: false,
            isPastClientHint: r.isPastClient,
            lifecycleStage: 'Past Client',
            selected: Boolean(r.isPastClient && r.confidence !== 'low'),
          };
        });
      }

      setCandidates(scored);
      setStep(4);
      setFilterGroup('likely');
      setOnlyMissingPhone(false);
    } catch (err) {
      console.error(err);
      setParseError(err.message || 'Classification failed');
      setStep(2);
    } finally {
      setIsBusy(false);
      setStatusMessage('');
      setProgress({ done: 0, total: 0 });
    }
  };

  const grouped = useMemo(() => {
    const likely = [];
    const maybe = [];
    const unlikely = [];
    for (const c of candidates) {
      if (c.confidence === 'junk') {
        unlikely.push(c);
      } else if (c.confidence === 'high' || (c.isPastClientHint && c.confidence === 'medium')) {
        likely.push(c);
      } else if (c.confidence === 'medium') {
        maybe.push(c);
      } else {
        unlikely.push(c);
      }
    }
    return { likely, maybe, unlikely };
  }, [candidates]);

  const visibleRows = useMemo(() => {
    const groupRows = grouped[filterGroup] || [];
    return onlyMissingPhone ? groupRows.filter((c) => !hasPhone(c)) : groupRows;
  }, [grouped, filterGroup, onlyMissingPhone]);
  const selectedCount = candidates.filter((c) => c.selected).length;
  const selectedMissingPhone = candidates.filter((c) => c.selected && !hasPhone(c)).length;
  const likelyMissingPhone = grouped.likely.filter((c) => !hasPhone(c)).length;

  const toggleSelected = (rowIndex, value) => {
    setCandidates((prev) =>
      prev.map((c) => (c.rowIndex === rowIndex ? { ...c, selected: value } : c))
    );
  };

  const selectAllInGroup = (groupKey, value) => {
    const ids = new Set((grouped[groupKey] || []).map((c) => c.rowIndex));
    setCandidates((prev) =>
      prev.map((c) => (ids.has(c.rowIndex) ? { ...c, selected: value } : c))
    );
  };

  const handleImport = async () => {
    const approved = candidates.filter((c) => c.selected);
    if (approved.length === 0) {
      alert('Select at least one contact to import.');
      return;
    }

    setIsBusy(true);
    setStatusMessage(`Importing ${approved.length} past client${approved.length === 1 ? '' : 's'}…`);
    try {
      const { data: results, error } = await importPastClients(
        approved.map((c) => ({
          name: c.name,
          email: c.email,
          phone: c.phone,
          location: c.location,
          notes: c.notes,
          birthday: c.birthday,
          homeClosingDate: c.homeClosingDate,
          tags: c.tags,
          lifecycleStage: 'Past Client',
          engagementLevel: c.engagementLevel || 'Medium',
        })),
        { defaultLifecycleStage: 'Past Client', updateExisting: true }
      );

      if (error) throw new Error(error.message);
      await onComplete?.(results);
      handleClose();
    } catch (err) {
      console.error(err);
      alert(err.message || 'Import failed');
    } finally {
      setIsBusy(false);
      setStatusMessage('');
    }
  };

  if (!isOpen) return null;

  const stepLabel =
    step === 1 ? 'Upload your list' :
    step === 2 ? 'Confirm columns' :
    step === 3 ? 'Finding past clients' :
    'Review & import';

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-3xl max-h-[90vh] overflow-hidden elevation-4 flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Find My Past Clients</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Step {Math.min(step, 3)} of 3: {stepLabel}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={handleClose} disabled={isBusy}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="px-6 py-3 bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-center space-x-3">
            {[1, 2, 3].map((s) => {
              const active = step === 3 ? s <= 2 : (step === 4 ? s <= 3 : s <= step);
              const done = step === 4 ? s < 3 : (step === 3 ? s < 2 : s < step);
              return (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
                  }`}>
                    {done ? <Icon name="Check" size={16} /> : s}
                  </div>
                  {s < 3 && (
                    <div className={`w-10 h-0.5 mx-2 ${active && s < (step === 4 ? 3 : step) ? 'bg-primary' : 'bg-muted'}`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="p-6 overflow-y-auto flex-1 min-h-0">
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-center">
                <h3 className="font-medium text-foreground mb-2">Upload any CRM or spreadsheet export</h3>
                <p className="text-sm text-muted-foreground mb-4 max-w-lg mx-auto">
                  Drop in a messy database export. We&apos;ll find the people who look like past clients
                  (closed deals) so you only import the ones you actually want to nurture.
                </p>
                <div className="border-2 border-dashed border-border rounded-lg p-8 text-center max-w-lg mx-auto">
                  <Icon name="Upload" size={40} className="mx-auto text-primary mb-4" />
                  <p className="text-sm font-medium text-foreground">
                    {file ? file.name : 'Choose a CSV or Excel file'}
                  </p>
                  {rows.length > 0 && (
                    <p className="text-xs text-success mt-1">
                      ✓ {headers.length} columns, {rows.length.toLocaleString()} rows
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-2 mb-4">Supported: .csv, .xlsx</p>
                  <label className="inline-flex">
                    <input
                      type="file"
                      accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                      onChange={handleFileUpload}
                      disabled={isBusy}
                      className="sr-only"
                    />
                    <span className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground cursor-pointer hover:opacity-90 transition-opacity">
                      <Icon name="FolderOpen" size={16} />
                      {file ? 'Choose a different file' : 'Choose file'}
                    </span>
                  </label>
                </div>
              </div>

              {oversizeWarning && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 max-w-lg mx-auto text-left">
                  <div className="flex items-start space-x-2">
                    <Icon name="AlertTriangle" size={16} className="text-warning mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-warning">Large file detected ({rows.length.toLocaleString()} rows)</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        This tool works best with your past-client list — not your entire lead database.
                        If you can, re-export just closed / past clients from your CRM (files over {OVERSIZE_ROW_WARNING.toLocaleString()} rows may be slow).
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Google Review / AUTO Review Ask warning — temporarily hidden
              {autoReviewAskOn && !dismissReviewAskWarning && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 max-w-lg mx-auto text-left">
                  <div className="flex items-start gap-2">
                    <Icon name="Info" size={16} className="text-amber-700 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-amber-900">AUTO Review Ask is currently on</p>
                      <p className="text-sm text-amber-800 mt-1">
                        Optional tip: you can turn AUTO Review Ask off before a bulk upload, import this list,
                        then turn it on and start the Review Ask campaign afterward. You do not have to —
                        you can keep it on and continue.
                      </p>
                      <p className="text-sm text-amber-800 mt-2">
                        Asking again is not all bad — it can start a good conversation and will not hurt.
                      </p>
                      <button
                        type="button"
                        className="text-xs text-amber-900 underline mt-2"
                        onClick={() => setDismissReviewAskWarning(true)}
                      >
                        Dismiss
                      </button>
                    </div>
                  </div>
                </div>
              )}
              */}

              {parseError && (
                <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 max-w-lg mx-auto text-left">
                  <p className="text-sm font-medium text-destructive">{parseError}</p>
                </div>
              )}

              <div className="bg-muted/30 rounded-lg p-4 text-sm text-muted-foreground space-y-1 max-w-lg mx-auto text-left">
                <p className="font-medium text-foreground">Tips for best results</p>
                <p>• Include stage/status and closing date columns when available</p>
                <p>• Phone numbers are required for SMS nurture</p>
                <p>• Prefer a filtered “closed / past client” export if your CRM allows it</p>
              </div>

              {onSwitchClassic && (
                <div className="text-center">
                <button
                  type="button"
                  className="text-sm text-primary underline"
                  onClick={onSwitchClassic}
                >
                  Prefer the classic CSV import instead?
                </button>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-foreground mb-2">Confirm column mapping</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  We auto-detected these columns. Adjust anything that looks wrong.
                </p>
                {mappingNotes && (
                  <p className="text-xs text-muted-foreground italic mb-4">{mappingNotes}</p>
                )}
              </div>

              {pastClientStageValues.length > 0 && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-3 text-sm">
                  <span className="font-medium text-success">Past-client stage values detected: </span>
                  <span className="text-muted-foreground">{pastClientStageValues.join(', ')}</span>
                </div>
              )}

              <div className="space-y-3">
                {headers.map((header, index) => (
                  <div key={`${header}-${index}`} className="flex items-center space-x-4">
                    <div className="w-1/3">
                      <label className="text-sm font-medium text-foreground truncate block" title={header}>
                        {header}
                      </label>
                    </div>
                    <div className="w-2/3">
                      <Select
                        placeholder="Select field…"
                        options={SYSTEM_FIELDS}
                        value={mappings[header] || ''}
                        onChange={(value) =>
                          setMappings((prev) => ({ ...prev, [header]: value }))
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="flex flex-col items-center justify-center py-16 space-y-4">
              <Icon name="Loader2" size={40} className="text-primary animate-spin" />
              <p className="text-sm font-medium text-foreground">{statusMessage || 'Working…'}</p>
              {progress.total > 0 && (
                <div className="w-64">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary transition-all"
                      style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    {progress.done} / {progress.total}
                  </p>
                </div>
              )}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4">
              <div>
                <h3 className="font-medium text-foreground mb-1">Review suggested past clients</h3>
                <p className="text-sm text-muted-foreground">
                  {selectedCount} selected · Only checked contacts will be imported as Past Clients.
                </p>
              </div>

              {(selectedMissingPhone > 0 || likelyMissingPhone > 0) && (
                <div className="bg-warning/10 border border-warning/20 rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Icon name="PhoneOff" size={16} className="text-warning mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0 text-sm">
                      <p className="font-medium text-warning">
                        {selectedMissingPhone} of {selectedCount} selected contact{selectedCount === 1 ? '' : 's'} {selectedMissingPhone === 1 ? 'has' : 'have'} no phone number
                      </p>
                      <p className="text-muted-foreground mt-1">
                        They&apos;ll still be imported, but NurtureFlow nurtures by text — they won&apos;t receive
                        any messages until a phone number is added. Rows missing a phone are marked below.
                      </p>
                      <label className="inline-flex items-center gap-2 mt-2 text-xs text-foreground cursor-pointer">
                        <input
                          type="checkbox"
                          className="rounded border-border"
                          checked={onlyMissingPhone}
                          onChange={(e) => setOnlyMissingPhone(e.target.checked)}
                        />
                        Only show contacts missing a phone
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {[
                  { key: 'likely', label: 'Likely', count: grouped.likely.length },
                  { key: 'maybe', label: 'Maybe', count: grouped.maybe.length },
                  { key: 'unlikely', label: 'Unlikely', count: grouped.unlikely.length },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setFilterGroup(tab.key)}
                    className={`px-3 py-1.5 rounded-full text-sm border ${
                      filterGroup === tab.key
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-muted/40 text-foreground border-border'
                    }`}
                  >
                    {tab.label} ({tab.count})
                  </button>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  className="text-xs text-primary"
                  onClick={() => selectAllInGroup(filterGroup, true)}
                >
                  Select all in this group
                </button>
                <button
                  type="button"
                  className="text-xs text-muted-foreground"
                  onClick={() => selectAllInGroup(filterGroup, false)}
                >
                  Clear group
                </button>
              </div>

              <div className="border border-border rounded-lg divide-y divide-border max-h-[42vh] overflow-y-auto">
                {visibleRows.length === 0 ? (
                  <p className="p-4 text-sm text-muted-foreground">No contacts in this group.</p>
                ) : (
                  visibleRows.map((c) => (
                    <div key={c.rowIndex} className="p-3 flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="mt-1 rounded border-border"
                        checked={Boolean(c.selected)}
                        onChange={(e) => toggleSelected(c.rowIndex, e.target.checked)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">{c.name || 'Unnamed'}</p>
                          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded ${
                            c.confidence === 'high' ? 'bg-success/15 text-success' :
                            c.confidence === 'medium' ? 'bg-warning/15 text-warning' :
                            'bg-muted text-muted-foreground'
                          }`}                          >
                            {c.confidence}
                          </span>
                          {!hasPhone(c) && (
                            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-warning/15 text-warning">
                              <Icon name="PhoneOff" size={10} />
                              No phone
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {hasPhone(c) ? c.phone : <span className="text-warning">No phone — can&apos;t receive texts</span>}
                          {c.email ? ` · ${c.email}` : ''}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">{c.reason}</p>
                      </div>
                      <span className="text-xs text-muted-foreground flex-shrink-0">Past Client</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border flex-shrink-0 bg-card">
          <Button
            variant="outline"
            onClick={() => {
              if (step === 1) handleClose();
              else if (step === 2) setStep(1);
              else if (step === 4) setStep(2);
            }}
            disabled={isBusy || step === 3}
          >
            {step === 1 ? 'Cancel' : 'Back'}
          </Button>

          <div className="flex items-center gap-3">
            {statusMessage && step !== 3 && (
              <span className="text-xs text-muted-foreground hidden sm:inline">{statusMessage}</span>
            )}
            {step === 1 && (
              <Button
                variant="default"
                onClick={inferMapping}
                disabled={!file || rows.length === 0 || isBusy}
                loading={isBusy}
                iconName="Sparkles"
                iconPosition="left"
              >
                Detect columns
              </Button>
            )}
            {step === 2 && (
              <Button
                variant="default"
                onClick={runClassification}
                disabled={!hasNameMapping || isBusy}
                iconName="ArrowRight"
                iconPosition="right"
              >
                Find past clients
              </Button>
            )}
            {step === 4 && (
              <Button
                variant="default"
                onClick={handleImport}
                disabled={selectedCount === 0 || isBusy}
                loading={isBusy}
                iconName="Upload"
                iconPosition="left"
              >
                Import {selectedCount} selected
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindPastClientsWizard;
